// Preview de la landing pública — fetch al endpoint /public/lm/<slug>.
// Permite al admin verificar que el slug + headline + consent_copy se ven bien
// antes de compartir la URL.

import { useEffect, useState } from 'react';

import { leadMagnetsApi, LeadMagnetsApiError } from '../api';
import type { PublicLanding } from '../types';

interface Props {
  slug: string | null;
  publicUrl?: string;
}

export function LandingPreview({ slug, publicUrl }: Props) {
  const [data, setData] = useState<PublicLanding | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    leadMagnetsApi
      .getPublicLanding(slug)
      .then(setData)
      .catch((e: unknown) => {
        const msg =
          e instanceof LeadMagnetsApiError ? e.message : 'Error desconocido';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (!slug) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Selecciona un magnet para ver el preview.
      </div>
    );
  }
  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Cargando preview…</div>;
  }
  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Error: {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3 p-4 border rounded-md bg-card max-w-2xl">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Preview de landing</h3>
        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs underline text-primary"
          >
            Abrir landing
          </a>
        )}
      </div>
      <div className="border rounded p-6 bg-muted/30">
        <h1 className="text-2xl font-bold">
          {data.landing_headline || data.title}
        </h1>
        <p className="mt-3 text-sm text-foreground whitespace-pre-wrap">
          {data.description || 'Sin descripción.'}
        </p>
        <div className="mt-6 space-y-2">
          <input
            type="email"
            placeholder="tu@correo.com"
            disabled
            className="w-full border rounded px-3 py-2 bg-card"
          />
          <button
            type="button"
            disabled
            className="w-full px-4 py-2 rounded bg-primary text-primary-foreground text-sm"
          >
            {data.has_gated_asset ? 'Quiero el recurso' : 'Apuntarme'}
          </button>
          <p className="text-xs text-muted-foreground">{data.consent_copy}</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Tipo: <span className="font-mono">{data.type}</span>
        {data.has_gated_asset && ' · entrega un asset descargable'}
      </p>
    </div>
  );
}
